import {alpha, lighten} from '@mui/material/styles';
import {chipClasses} from '@mui/material/Chip';

import {red, gray, green, blue, warningOrange} from '../themePrimitives';

export const surfacesCustomizations = {
    MuiAvatar: {
        styleOverrides: {
            root: {
                fontSize: '1rem'
            }
        }
    },
    MuiAccordion: {
        defaultProps: {
            elevation: 0,
            disableGutters: true
        },
        styleOverrides: {
            root: ({theme}) => ({
                padding: 4,
                overflow: 'clip',
                backgroundColor: (theme.vars || theme).palette.background.paper,
                border: '1px solid',
                borderColor: (theme.vars || theme).palette.divider,
                ':before': {
                    backgroundColor: 'transparent'
                },
                '&:first-of-type': {
                    borderTopLeftRadius: (theme.vars || theme).shape.borderRadius,
                    borderTopRightRadius: (theme.vars || theme).shape.borderRadius,
                    '& .MuiAccordionSummary-root': {
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4
                    }
                },
                '&:last-of-type': {
                    borderBottomLeftRadius: (theme.vars || theme).shape.borderRadius,
                    borderBottomRightRadius: (theme.vars || theme).shape.borderRadius,
                    '& .MuiAccordionSummary-root': {
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4
                    }
                },
                '&:not(:last-of-type)': {
                    borderBottom: 'none'
                },
                variants: [
                    {
                        props: {
                            severity: 'info'
                        },
                        style: {
                            backgroundColor: alpha(blue[50], 0.8),
                            border: `1px solid ${alpha(blue[200], 0.5)}`,
                            '& .MuiAccordionSummary-root': {
                                '&:hover': {backgroundColor: alpha(blue[100], 0.9)}
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'info-light'
                        },
                        style: {
                            backgroundColor: alpha(blue[50], 0.4),
                            border: `1px solid ${alpha(blue[200], 0.3)}`,
                            '& .MuiAccordionSummary-root': {
                                '&:hover': {backgroundColor: alpha(blue[100], 0.5)}
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'warning-light'
                        },
                        style: {
                            backgroundColor: alpha(warningOrange.surface, 0.4),
                            border: `1px solid ${alpha(warningOrange.border, 0.3)}`,
                            '& .MuiAccordionSummary-root': {
                                '&:hover': {backgroundColor: alpha(warningOrange.tint, 0.3)}
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'success'
                        },
                        style: {
                            backgroundColor: alpha(green[100], 0.4),
                            border: `1px solid ${alpha(green[300], 0.5)}`,
                            '& .MuiAccordionSummary-root': {
                                '&:hover': {backgroundColor: alpha(green[100], 0.6)}
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'error'
                        },
                        style: {
                            backgroundColor: alpha(red[50], 0.4),
                            border: `1px solid ${alpha(red[200], 0.3)}`,
                            '& .MuiAccordionSummary-root': {
                                '&:hover': {backgroundColor: alpha(red[100], 0.1)}
                            }
                        }
                    }
                ]
            })
        }
    },
    MuiAccordionSummary: {
        styleOverrides: {
            root: () => ({
                border: 'none',
                '&:hover': {backgroundColor: gray[50]},
                '&:focus-visible': {backgroundColor: 'transparent'}
            })
        }
    },
    MuiAccordionDetails: {
        styleOverrides: {
            root: {mb: 20, border: 'none'}
        }
    },
    MuiPaper: {
        defaultProps: {
            elevation: 0
        }
    },
    MuiCard: {
        styleOverrides: {
            root: ({theme}) => {
                return {
                    // padding: 16,
                    // gap: 16,
                    transition: 'all 100ms ease',
                    backgroundColor: gray[50],
                    borderRadius: (theme.vars || theme).shape.borderRadius,
                    borderColor: (theme.vars || theme).palette.divider,
                    boxShadow: 'none',
                    variants: [
                        {
                            props: {
                                variant: 'outlined'
                            },
                            style: {
                                border: `1px solid ${(theme.vars || theme).palette.divider}`,
                                boxShadow: 'none',
                                background: 'hsl(0, 0%, 100%)'
                            }
                        },
                        // Plain outlined card with a thicker (2px) border — no tint or gradient.
                        // Use for a card that should read as neutral but carry a little more
                        // presence than the default hairline outline. Pair with `variant="outlined"`;
                        // override `borderColor` via `sx` to tint the border (e.g. info.main).
                        {
                            props: {severity: 'outline'},
                            style: {
                                borderWidth: 2,
                                borderStyle: 'solid',
                                borderColor: (theme.vars || theme).palette.divider,
                                background: 'hsl(0, 0%, 100%)'
                            }
                        },
                        {
                            props: {
                                variant: 'outlined',
                                severity: 'error'
                            },
                            style: {
                                backgroundColor: red[50],
                                border: `1px solid ${alpha(red[100], 0.5)}`,
                                '& .MuiAlert-icon': {
                                    color: red[500]
                                },
                                '& .MuiButton-text': {
                                    '&:hover': {
                                        backgroundColor: alpha(red[100], 0.5)
                                    }
                                },
                                '& .MuiCardMedia-root': {
                                    backgroundColor: alpha(red[100], 0.5)
                                }
                            }
                        },
                        {
                            props: {
                                variant: 'outlined',
                                severity: 'warning'
                            },
                            style: {
                                backgroundColor: alpha(warningOrange.surface, 0.7),
                                border: `1px solid ${alpha(warningOrange.border, 0.5)}`,
                                '& .MuiAlert-icon': {
                                    color: warningOrange.icon
                                },
                                '& .MuiButton-text': {
                                    '&:hover': {
                                        backgroundColor: alpha(warningOrange.tint, 0.5)
                                    }
                                },
                                '& .MuiIconButton-root': {
                                    backgroundColor: alpha(warningOrange.tint, 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(warningOrange.tint, 0.9)
                                    }
                                },
                                '& .MuiCardMedia-root': {
                                    backgroundColor: alpha(warningOrange.tint, 0.5)
                                }
                            }
                        },
                        {
                            props: {
                                severity: 'success'
                            },
                            style: {
                                backgroundColor: green[100],
                                border: `1px solid ${alpha(green[300], 0.5)}`,
                                '& .MuiAlert-icon': {
                                    color: green[500]
                                },
                                '& .MuiSvgIcon-root': {
                                    fill: green[500]
                                },
                                '& .MuiButton-text': {
                                    '&:hover': {
                                        backgroundColor: alpha(green[300], 0.5)
                                    }
                                },
                                '& .MuiButton-colorInherit': {
                                    backgroundColor: alpha(green[200], 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(green[300], 0.5)
                                    }
                                }
                            }
                        },
                        {
                            props: {
                                severity: 'info'
                            },
                            style: {
                                backgroundColor: lighten(blue[50], 0.5),
                                border: `1px solid ${alpha(blue[300], 0.5)}`,
                                '& .MuiAlertTitle-root': {
                                    color: blue[500]
                                },
                                '& .MuiAlert-icon': {
                                    color: blue[500]
                                },
                                '& .MuiButton-text': {
                                    '&:hover': {
                                        backgroundColor: alpha(blue[200], 0.5)
                                    }
                                },
                                '& .MuiButton-colorInherit': {
                                    backgroundColor: alpha(blue[200], 0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(blue[300], 0.5)
                                    }
                                },
                                [`& .${chipClasses.root}`]: {
                                    color: alpha('#000', 0.6),
                                    backgroundColor: alpha(blue[300], 0.5)
                                }
                            }
                        },
                        // Gradient severities: same border + top colour as the regular severity
                        // card, with the fill fading a touch *lighter* toward the bottom for a
                        // subtle sheen. A gradient card therefore reads identically to the flat
                        // card at the top edge, just softening as it goes down. Used for
                        // status-aware cards (e.g. apparatus needing review / in progress /
                        // overdue). Pair with `variant="outlined"`. The `-full` suffix stretches the
                        // lightening over the full card height instead of settling by 55%.
                        {
                            props: {severity: 'warning-gradient'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(warningOrange.border, 0.5),
                                background: `linear-gradient(180deg, ${alpha(warningOrange.surface, 0.7)} 0%, ${alpha(warningOrange.surface, 0.35)} 55%)`
                            }
                        },
                        {
                            props: {severity: 'warning-gradient-full'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(warningOrange.border, 0.5),
                                background: `linear-gradient(180deg, ${alpha(warningOrange.surface, 0.7)} 0%, ${alpha(warningOrange.surface, 0.35)} 100%)`
                            }
                        },
                        {
                            props: {severity: 'info-gradient'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(blue[300], 0.5),
                                background: `linear-gradient(180deg, ${lighten(blue[50], 0.5)} 0%, ${lighten(blue[50], 0.75)} 55%)`
                            }
                        },
                        {
                            props: {severity: 'info-gradient-full'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(blue[300], 0.5),
                                background: `linear-gradient(180deg, ${lighten(blue[50], 0.5)} 0%, ${lighten(blue[50], 0.75)} 100%)`
                            }
                        },
                        {
                            props: {severity: 'error-gradient'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(red[100], 0.5),
                                background: `linear-gradient(180deg, ${red[50]} 0%, ${lighten(red[50], 0.5)} 55%)`
                            }
                        },
                        {
                            props: {severity: 'error-gradient-full'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(red[100], 0.5),
                                background: `linear-gradient(180deg, ${red[50]} 0%, ${lighten(red[50], 0.5)} 100%)`
                            }
                        },
                        {
                            props: {severity: 'success-gradient'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(green[300], 0.5),
                                background: `linear-gradient(180deg, ${green[100]} 0%, ${lighten(green[100], 0.5)} 55%)`
                            }
                        },
                        {
                            props: {severity: 'success-gradient-full'},
                            style: {
                                borderWidth: 2,
                                borderColor: alpha(green[300], 0.5),
                                background: `linear-gradient(180deg, ${green[100]} 0%, ${lighten(green[100], 0.5)} 100%)`
                            }
                        }
                    ]
                };
            }
        }
    },
    MuiCardContent: {
        styleOverrides: {
            root: {
                // padding: 0,
                '&:last-child': {paddingBottom: 16}
            }
        }
    }
    // MuiCssBaseline: {
    //     styleOverrides: {
    //         body: theme => ({
    //             backgroundColor: (theme.vars || theme).palette.background.paper
    //         })
    //     }
    // }
};
